import * as React from 'react';
import { useWatch, type Control, type FieldValues } from 'react-hook-form';
import { Banner, type BannerVariant } from '../layout/Banner';
import { Stack } from '../layout/Stack';
import { Typography } from '../primitives';
import { rem } from '../tokens/spacing';

export interface InfoBannerMessage {
  lead: string;
  body: string;
}

export interface RhfInfoBannerProps {
  control: Control<FieldValues>;
  watchField: string;
  variant?: BannerVariant;
  semanticId?: string;
  className?: string;
  messageWhenTrue?: InfoBannerMessage;
  messageWhenFalse?: InfoBannerMessage;
}

function resolveMessage(
  checked: boolean,
  messageWhenTrue?: InfoBannerMessage,
  messageWhenFalse?: InfoBannerMessage,
): InfoBannerMessage | null {
  const message = checked ? messageWhenTrue : messageWhenFalse;
  if (!message?.lead && !message?.body) return null;
  return message;
}

export const RhfInfoBanner = React.memo(function RhfInfoBanner({
  control,
  watchField,
  variant = 'info',
  semanticId,
  className,
  messageWhenTrue,
  messageWhenFalse,
}: RhfInfoBannerProps): React.ReactElement | null {
  const watched = useWatch({ control, name: watchField });
  const checked = Boolean(watched);
  const message = resolveMessage(checked, messageWhenTrue, messageWhenFalse);

  if (message == null) {
    return null;
  }

  return (
    <Banner variant={variant} semanticId={semanticId} className={className} style={{ alignItems: 'flex-start' }}>
      <Stack gap={rem(4)} align='start'>
        <Typography variant='body-s' as='p' style={{ margin: 0 }}>
          <strong>{message.lead}</strong>
        </Typography>
        {message.body ? (
          <Typography variant='body-s' as='p' style={{ margin: 0 }}>
            {message.body}
          </Typography>
        ) : null}
      </Stack>
    </Banner>
  );
});

RhfInfoBanner.displayName = 'RhfInfoBanner';
